import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const relevantAgents = [
  { label: 'Interview Coach Agent', path: '/agents/interview-agent', desc: 'Automated interview question generation, candidate scoring, structured feedback, and interview workflow management.' },
  { label: 'Recruiter Agent', path: '/agents/recruiter-agent', desc: 'End-to-end recruiter workflow automation: candidate screening, shortlisting, outreach, and pipeline management.' },
];

const useCases = [
  { title: 'High-Volume Candidate Screening', desc: 'Screen hundreds of applicants automatically, ranking candidates by fit score, skills match, and role requirements.' },
  { title: 'Structured Interview Automation', desc: 'Generate role-specific interview questions, conduct structured assessments, and produce candidate feedback reports.' },
  { title: 'Recruiter Workflow Automation', desc: 'Automate recruiter tasks including job brief processing, sourcing, outreach drafts, and interview scheduling.' },
  { title: 'Onboarding Document Processing', desc: 'Process employment contracts, background check requests, and onboarding forms with document extraction automation.' },
  { title: 'Talent Pipeline Reporting', desc: 'Generate pipeline status reports, time-to-hire analytics, and diversity data summaries for HR leadership.' },
  { title: 'BPO Hiring at Scale', desc: 'Support BPO and shared services hiring programs in the Philippines and across SEA with AI-driven screening at scale.' },
];

const faqs = [
  {
    q: 'Can Eagentix automate the job interview process?',
    a: 'Yes. The Interview Coach Agent generates role-specific interview questions, evaluates candidate responses, assigns structured scores, and produces written feedback — reducing interviewer preparation time significantly.',
  },
  {
    q: 'How does the Recruiter Agent help HR teams?',
    a: 'The Recruiter Agent automates sourcing, candidate screening, shortlisting, outreach drafting, and pipeline tracking — allowing recruiters to focus on high-value interactions and final hiring decisions.',
  },
  {
    q: 'Is Eagentix suitable for large-scale BPO hiring in Southeast Asia?',
    a: 'Yes. Eagentix agents are designed for high-volume, repeatable hiring workflows typical in BPO and shared services operations in the Philippines, Malaysia, and across Southeast Asia.',
  },
  {
    q: 'Can Eagentix integrate with existing HRIS and ATS systems?',
    a: 'Yes. Eagentix agents can integrate with HRIS platforms, applicant tracking systems, and custom HR workflows via API. Contact us for specific integration requirements.',
  },
  {
    q: 'How does Eagentix ensure fair and unbiased AI hiring?',
    a: 'Eagentix agents use structured scoring criteria defined by your HR team. Human-in-the-loop approval gates ensure final hiring decisions remain with qualified HR professionals, not AI systems.',
  },
];

export default function HRRecruitmentAI() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="AI Agents for HR and Recruitment | Interview & Recruiter Automation | Eagentix"
        description="Eagentix AI agents automate candidate screening, structured interviews, recruiter workflows, and HR operations for enterprise and BPO hiring teams."
        pathname="/industries/hr-recruitment-ai-agents"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Enterprise AI · HR & Recruitment
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              AI Agents for HR and Recruitment
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Eagentix provides AI agents that automate interview workflows, candidate screening, and end-to-end recruiter operations — enabling HR teams to scale hiring without scaling headcount.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Relevant agents */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Agents Built for HR and Recruitment
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Two specialised agents that cover the core automation priorities of modern HR and recruitment teams.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {relevantAgents.map((agent, i) => (
              <Link
                key={i}
                to={agent.path}
                style={{ ...card, textDecoration: 'none', display: 'block' }}
              >
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0071e3', marginBottom: '10px' }}>{agent.label}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{agent.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Use Cases in HR and Recruitment
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Where HR teams and recruitment firms across Southeast Asia deploy Eagentix agents to reduce time-to-hire and improve candidate quality.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {useCases.map((uc, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>{uc.title}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Frequently Asked Questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Scale Your Hiring with AI
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Deploy AI agents for interview automation, candidate screening, and recruiter workflows — and reduce time-to-hire across your organisation.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
